import { useCMSPageField } from '@/cms/fields'
import { CMSMediaType } from '@/cms/fields/mappers/productDetailPageFieldsMapper'
import { useAddFavoriteFromUrl } from '@/domain/product/hooks'
import { useIntl } from '@/intl'
import { useOnScreen } from '@/scopes/common/components/hooks/useOnScreen'
import { LazyImage } from '@/scopes/common/components/Image'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import { mqMin, space, tokPx } from '@sunwebgroup/ui-components'
import { Button, Icon, IconButton, Tag } from '@sunwebgroup/ui-components/uds'
import { Sheet } from '@sunwebgroup/ui-components/uds/index'
import React, { useEffect, useRef, useState } from 'react'
import { css, styled } from 'styled-components'
import { Carousel, useCarouselItemsInView } from '../../Carousel'
import { FullScreenGallery } from '../../FullScreenGallery'
import { EXTRA_L_MIN_BREAKPOINT } from '../utils'
import FavoriteButton from './Favorite/FavoriteButton'
import { GalleryLoading } from './GalleryLoading'

const ImageElementButtonModal = styled.div.attrs(() => ({
  role: 'button',
  tabIndex: -1,
}))`
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease-in-out;

  &:hover {
    filter: brightness(0.85);
  }
`

const StyledIconButton = styled(IconButton)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const StyledAllPhotosButton = styled(Button)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: max-content;
  z-index: 1;
`

const LazyImageElementWrapper = styled.div`
  width: 280px;
  height: 210px;
  position: relative;
`

export const ImageElementButton = styled.div.attrs(() => ({
  role: 'button',
  tabIndex: -1,
}))`
  position: relative;
  display: block;
  border-radius: ${tokPx('primitives_corner_radius_m')};
  overflow: hidden;
  width: 100%;
  cursor: pointer;
  transition: all 0.3s ease-in-out;


  @media ${mqMin('primitives_breakpoint_m_min')} {
    border-radius: unset;
  }

  &:hover {
    filter: brightness(0.85);
  }
`

const FirstImageElementButton = styled(ImageElementButton)`
  display: block;
  aspect-ratio: 7 / 3;
  border-top-left-radius: ${tokPx('primitives_corner_radius_m')};
  border-top-right-radius: ${tokPx('primitives_corner_radius_m')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    width: 100%;
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    aspect-ratio: 16 / 9;
  }

  @media (min-width: ${EXTRA_L_MIN_BREAKPOINT}) {
    aspect-ratio: 7 / 3;
  }
`

const OtherImageElement = styled(ImageElementButton)`
  aspect-ratio: 4 / 3;

  &:last-of-type:after {
    content:'';
    position:absolute;
    left:0px;
    top:0px;
    width:100%;
    height:100%;
    background: rgba(0, 0, 0, 0.4);
  }
`

const StyledGridModal = styled.ol`
  display: flex;
  flex-wrap: wrap;
  margin: 0 auto;
  list-style: none;
  padding: unset;
  gap: ${space('primitives_space_2')};
`

const StyledImageWrapperModal = styled.li`
  width: calc(50% - (${space('primitives_space_2')} / 2));

  ${ImageElementButtonModal} {
    aspect-ratio: 1 / 1;
  }

  &:first-child,
  &:nth-child(3n + 1) {
    width: 100%;

    ${ImageElementButtonModal} {
      aspect-ratio: 16 / 9;
    }
  }

`

const WrapperDesktop = styled.div`
  gap: ${space('primitives_space_2')};
  flex-direction: column;
  display: none;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    display: flex;
  }
`

const WrapperMobile = styled.div`
  //to make it full width
  margin-left: -${space('primitives_space_16')};
  margin-right: -${space('primitives_space_16')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    display: none;
  }
`

const BigImageWrapper = styled.div`
  position: relative;
`

const OtherImagesWrapper = styled.div`
  display: flex;
  gap: ${space('primitives_space_2')};

  ${OtherImageElement} {
    &:first-child {
      @media ${mqMin('primitives_breakpoint_m_min')} {
        border-bottom-left-radius: ${tokPx('primitives_corner_radius_m')};
      }
    }

    &:last-child {
      @media ${mqMin('primitives_breakpoint_m_min')} {
        border-bottom-right-radius: ${tokPx('primitives_corner_radius_m')};
      }
    }
  }
`

const FavoriteContainer = styled.div`
  position: absolute;
  top: ${space('primitives_space_8')};
  right: ${space('primitives_space_8')};
`

const CarouselElement = styled.div`
  position: relative;

  &:last-of-type{
    ${ImageElementButton}:after {
      content:'';
      position:absolute;
      left:0px;
      top:0px;
      width:100%;
      height:100%;
      background: rgba(0, 0, 0, 0.4);
    }
  }
`

const CollectionTag = styled(Tag)<{ $textColor?: string, $backgroundColor?: string }>`
position: absolute;
  top: ${space('primitives_space_8')};
  left: ${space('primitives_space_8')};
  pointer-events: none;
  z-index: 1;

  ${({ $textColor, $backgroundColor }) => css`
    background: ${$backgroundColor};
    color: ${$textColor};
  `}
`

interface SentinelProps {
  onVisible: () => void
}

/**
 * A sentinel component that triggers a callback when it becomes visible on the screen.
 * @param onVisible - The callback function to be called when the sentinel becomes visible.
 * @returns The sentinel component.
 */
function Sentinel({ onVisible }: SentinelProps) {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const isSentinelVisible = useOnScreen(sentinelRef)

  useEffect(() => {
    if (isSentinelVisible) {
      onVisible()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We know onVisible is not going to change.
  }, [isSentinelVisible])

  return <div ref={sentinelRef} />
}

const StyledSentinel = styled(Sentinel)`
  background: transparent;
  height: 1px;
  margin-bottom: -1px;
  width: 100%;
`

export default function Gallery() {
  const intl = useIntl()
  const media = useCMSPageField('PDP.media')
  const collectionName = useCMSPageField('PDP.collectionName')
  const collectionTheme = useCMSPageField('PDP.collectionTheme')
  const contextItemId = useCMSPageField('contextItemId')
  useAddFavoriteFromUrl(contextItemId)
  const maxElements = 6
  const modalTitle = intl.formatMessage({ id: 'accommodation_page_gallery_modal_title' })
  const [showModal, setShowModal] = useState(false)
  const [activeItem, setActiveItem] = useState(0)
  const [showDetail, setShowDetail] = useState(false)
  const [clickedItemIndex, setClickedItemIndex] = useState(0)
  const { sendEvent } = useAnalytics()
  const [show, setShow] = useState(false)
  const ref = useRef(null)
  const { isVisibleItem } = useCarouselItemsInView(ref)
  const sheetImageRef = useRef<HTMLDivElement>(null)
  const sheetImageInView = useOnScreen(sheetImageRef)

  useEffect(() => {
    if (showModal) {
      window.requestAnimationFrame(() => {
        const element = document.getElementById(`gallery-element-${activeItem}`)
        if (element) {
          element.scrollIntoView({ block: 'start' })
        }
      })
    }

    setShow(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this effect when the modal is opened
  }, [showModal])

  const closeModal = () => {
    setShowModal(false)
  }

  const openModal = (index: number) => {
    setShowModal(true)
    setActiveItem(index)
  }

  const closeDetail = () => {
    setShowDetail(false)
  }

  const openDetail = (index: number) => {
    setClickedItemIndex(index)
    setShowDetail(true)
  }

  const handleOnSettleDetail = () => {
    sendEvent(
      createPDPClickEvent({
        element: 'Slider',
        description: `Photo Gallery`,
        result: 'Image slider - Slide',
      }),
    )
  }

  const mediaFirstElement = media[0]
  const restMediaElements = media.slice(1, maxElements)

  const handleImageClick = (index: number) => {
    openModal(index)
    sendEvent(
      createPDPClickEvent({
        element: 'Element clicked',
        description: `Photo Gallery`,
        result: 'Collection gallery - Opens',
        type: `${index + 1}`,
      }),
    )
  }

  const handleDetailImageClick = (index: number) => {
    openDetail(index)
    sendEvent(
      createPDPClickEvent({
        element: 'Element clicked',
        description: `Photo Gallery`,
        result: 'Image slider - Opens',
        type: `${index + 1}`,
      }),
    )
  }

  const handleSentinelVisible = (): void => {
    sendEvent(
      createPDPClickEvent({
        element: 'Scroll',
        description: `Photo Gallery`,
        result: 'Collection gallery - Scroll to bottom',
      }),
    )
  }

  if (!show)
    return <GalleryLoading />

  if (!media || media.length === 0)
    return null

  return (
    <>
      <WrapperDesktop>
        <BigImageWrapper>
          <FirstImageElementButton onClick={() => handleImageClick(0)}>
            <LazyImage
              src={mediaFirstElement.src}
              srcSet={[1280, 480]}
              sizes="(width <= 600px) 480px, (width <= 840px) 1280px, 100vw"
              alt={mediaFirstElement.alt || ''}
            />
            {mediaFirstElement.type === CMSMediaType.VIDEO && (
              <StyledIconButton
                glyph="PlayFilled"
                type="overlay-neutral"
                ariaLabel="Play video"
                size="M"
              />
            )}
            {collectionName && (
              <CollectionTag size="M" $textColor={collectionTheme.text} $backgroundColor={collectionTheme.background}>{collectionName}</CollectionTag>
            )}
          </FirstImageElementButton>
          <FavoriteContainer>
            <FavoriteButton />
          </FavoriteContainer>
        </BigImageWrapper>
        <OtherImagesWrapper>
          {restMediaElements.map((element, index) => (
            <OtherImageElement onClick={() => handleImageClick(index + 1)} key={`other-media-${index + 1}`}>
              <LazyImage src={element.src} srcSet={[480]} alt={element.alt || ''} />
              {element.type === CMSMediaType.VIDEO && (
                <StyledIconButton
                  glyph="PlayFilled"
                  type="overlay-neutral"
                  ariaLabel="Play video"
                  size="M"
                />
              )}
              {index === restMediaElements.length - 1 && (
                <StyledAllPhotosButton $size="S" $type="overlayNeutral">
                  <Icon glyph="Grid" />
                  {modalTitle}
                </StyledAllPhotosButton>
              )}
            </OtherImageElement>
          ))}
        </OtherImagesWrapper>
      </WrapperDesktop>
      <WrapperMobile>
        <Carousel isFullWidth>
          {media.slice(0, maxElements).map((mediaElement, index) => (
            <CarouselElement key={`carousel-item-${index}`} ref={ref}>
              {index === 0 && collectionName && (
                <CollectionTag size="M" $textColor={collectionTheme.text} $backgroundColor={collectionTheme.background}>{collectionName}</CollectionTag>
              )}
              <ImageElementButton onClick={() => handleImageClick(index)}>
                <LazyImageElementWrapper>
                  <LazyImage
                    src={mediaElement.src}
                    srcSet={[480]} // srcset values are a bit larger compared to the sizes, because most images are served in 3/2 format, but shown in something like 4/3 format (depending on height of information on ProductCard)
                    loading={!isVisibleItem ? 'lazy' : 'eager'}
                  />
                </LazyImageElementWrapper>
              </ImageElementButton>
              {index === 0 && (
                <FavoriteContainer>
                  <FavoriteButton />
                </FavoriteContainer>
              )}
              {index === media.slice(0, maxElements).length - 1 && (
                <StyledAllPhotosButton $size="S" $type="overlayNeutral" onClick={() => handleImageClick(index)}>
                  <Icon glyph="Grid" />
                  {modalTitle}
                </StyledAllPhotosButton>
              )}
            </CarouselElement>
          ))}
        </Carousel>
      </WrapperMobile>
      <Sheet.Root open={showModal} onOpenChange={closeModal}>
        <Sheet.Body side="bottom">
          <Sheet.Header closeLabel={intl.formatMessage({ id: 'close' })} titleLabel={modalTitle} />
          <Sheet.Content>
            <StyledGridModal>
              {media.map((mediaElement, index) => (
                <StyledImageWrapperModal key={`media-${index}`}>
                  <ImageElementButtonModal onClick={() => handleDetailImageClick(index)} ref={sheetImageRef}>
                    <div id={`gallery-element-${index}`} />
                    <LazyImage
                      src={mediaElement.src}
                      srcSet={
                        index % 3 === 0
                          ? [1280, 960, 480]
                          : [960, 480]
                      }
                      loading={!sheetImageInView ? 'lazy' : 'eager'}
                    />
                    {mediaElement.type === CMSMediaType.VIDEO && (
                      <StyledIconButton
                        glyph="PlayFilled"
                        type="overlay-neutral"
                        ariaLabel="Play video"
                        size="M"
                      />
                    )}
                  </ImageElementButtonModal>
                </StyledImageWrapperModal>
              ))}
              <StyledSentinel onVisible={handleSentinelVisible} />
            </StyledGridModal>
          </Sheet.Content>
        </Sheet.Body>
      </Sheet.Root>
      <FullScreenGallery onClose={closeDetail} open={showDetail} startFromIndex={clickedItemIndex} media={media} onSettle={handleOnSettleDetail} />
    </>
  )
}
